import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { authAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Briefcase } from 'lucide-react';

const RecruiterLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      const response = await authAPI.login(formData);

      // Backend returns { success, user, token }
      if (!response?.success || !response?.user || !response?.token) {
        throw new Error('Invalid server response');
      }

      const { user, token } = response;

      if (user.role !== 'recruiter' && user.role !== 'super_admin') {
        toast({
          title: 'Access Denied',
          description: 'This login is for Recruiters only',
          variant: 'destructive',
        });
        return;
      }

      localStorage.setItem('authToken', token);
      localStorage.setItem('userEmail', user.email);
      localStorage.setItem('userFullName', user.fullName || '');
      localStorage.setItem('userRole', user.role);
      localStorage.setItem('companyId', user.companyId || '');

      toast({
        title: 'Success!',
        description: `Welcome back, ${user.fullName || ''}!`,
      });

      navigate('/recruiter/dashboard');

    } catch (error) {
      toast({
        title: 'Login Failed',
        description:
          error?.response?.data?.detail ||
          error.message ||
          'Invalid email or password',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-900 flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 bg-white/10 backdrop-blur-lg border-white/20">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Recruiter Portal
          </h1>
          <p className="text-gray-300">
            Post jobs and find the best talent
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email" className="text-gray-200">
              Email Address
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="recruiter@company.com"
              className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-gray-200">
              Password
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold h-12"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Authenticating...
              </>
            ) : (
              'Login as Recruiter'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <Link to="/admin/login" className="text-blue-300 hover:text-blue-200 text-sm block">
            Super Admin Login
          </Link>
          <Link to="/login" className="text-gray-400 hover:text-gray-300 text-sm block">
            Job Seeker Login
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default RecruiterLogin;